import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  ArrowRightIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-700' },
  sent: { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Kabul Edildi', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'İptal Edildi', color: 'bg-gray-200 text-gray-500' },
};

const fmt = (v) => parseFloat(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function QuotesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['quotes', statusFilter, search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);
      const res = await api.get(`/quotes?${params}`);
      return res.data;
    },
  });

  const quotes = data?.data || [];
  const pagination = data?.pagination || { total: 0, totalPages: 1 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teklifler</h1>
          <p className="text-sm text-gray-500 mt-1">
            Şehirlerarası ambulans teklif yönetimi
          </p>
        </div>
        <Link to="/quotes/new">
          <Button className="bg-medical-600 hover:bg-medical-700 text-white">
            <PlusIcon className="h-5 w-5 mr-2" />
            Yeni Teklif
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Teklif no, hasta adı, kurum ara..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'all', label: 'Tümü' },
            ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))
          ].map((s) => (
            <button
              key={s.value}
              onClick={() => { setStatusFilter(s.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s.value
                  ? 'bg-medical-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cards */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-medical-600" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Henüz teklif bulunmuyor</p>
          <Link to="/quotes/new" className="text-medical-600 hover:underline text-sm mt-2 inline-block">
            İlk teklifi oluştur
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {quotes.map((q) => {
            const st = STATUS_CONFIG[q.status] || STATUS_CONFIG.draft;
            return (
              <div
                key={q.id}
                onClick={() => navigate(`/quotes/${q.id}`)}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-medical-200 transition-all cursor-pointer group"
              >
                {/* Top row */}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-sm text-medical-700">{q.quote_number}</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${st.color}`}>
                    {st.label}
                  </span>
                </div>

                {/* Route */}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <MapPinIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <span className="truncate">{q.origin_city}/{q.origin_district}</span>
                  <ArrowRightIcon className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{q.destination_city}/{q.destination_district}</span>
                </div>

                {/* Distance */}
                <div className="text-xs text-gray-500 mb-3">
                  {q.distance_km} km
                  {q.toll_fee > 0 && ` • Otoyol: ${fmt(q.toll_fee_override || q.toll_fee)} TL`}
                </div>

                {/* Patient / Institution */}
                {(q.patient_name || q.institution_name) && (
                  <div className="text-xs text-gray-500 mb-3 truncate">
                    {q.patient_name && `${q.patient_name} ${q.patient_surname || ''}`}
                    {q.patient_name && q.institution_name && ' • '}
                    {q.institution_name}
                  </div>
                )}

                {/* Price */}
                <div className="flex items-end justify-between pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-400">
                    {new Date(q.created_at).toLocaleDateString('tr-TR')}
                  </div>
                  <div className="text-right">
                    {q.discount_enabled && parseFloat(q.discount_rate) > 0 && (
                      <div className="text-xs text-gray-400 line-through">{fmt(q.total_amount)} TL</div>
                    )}
                    <div className="text-lg font-bold text-gray-900 group-hover:text-medical-700 transition-colors">
                      {fmt(q.final_amount)} <span className="text-xs font-normal">TL</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 text-sm rounded border disabled:opacity-50"
          >
            Önceki
          </button>
          <span className="px-3 py-1 text-sm text-gray-600">
            {page} / {pagination.totalPages}
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 text-sm rounded border disabled:opacity-50"
          >
            Sonraki
          </button>
        </div>
      )}
    </div>
  );
}
